import {
  useState,
  useRef,
  type ChangeEvent,
  type FormEvent,
} from "react";
import type { IngredientGroup, RecipeStep } from "../types";

const IMAGES_BASE = import.meta.env.VITE_IMAGES_BASE_URL ?? "";

const COURSES = ["Breakfast", "Brunch", "Lunch", "Dinner", "Appetizer", "Side", "Dessert", "Snack", "Drink", "Other"];
const CUISINES = ["American", "French", "Italian", "Mexican", "Thai", "Japanese", "Chinese", "Indian", "Mediterranean", "Greek", "Korean", "Vietnamese", "Middle Eastern", "Other"];

export interface RecipeFormValues {
  title: string;
  slug: string;
  description: string;
  course: string;
  cuisine: string;
  prepTimeMinutes: number;
  cookTimeMinutes: number;
  servings: number;
  tags: string[];
  ingredients: IngredientGroup[];
  steps: RecipeStep[];
  notes: string;
  source: string;
  sourceUrl: string;
  rating: number;
  isFavorite: boolean;
  imageKey: string;
  thumbnailKey: string;
}

interface Props {
  initialValues?: Partial<RecipeFormValues>;
  onSubmit: (values: RecipeFormValues, imageFile: File | null) => Promise<void>;
  submitLabel: string;
}

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function RecipeForm({ initialValues, onSubmit, submitLabel }: Props) {
  const [title, setTitle] = useState(initialValues?.title ?? "");
  const [slug, setSlug] = useState(initialValues?.slug ?? "");
  const [slugEdited, setSlugEdited] = useState(!!initialValues?.slug);
  const [description, setDescription] = useState(initialValues?.description ?? "");
  const [course, setCourse] = useState(initialValues?.course ?? "");
  const [cuisine, setCuisine] = useState(initialValues?.cuisine ?? "");
  const [prepTime, setPrepTime] = useState(initialValues?.prepTimeMinutes ?? 0);
  const [cookTime, setCookTime] = useState(initialValues?.cookTimeMinutes ?? 0);
  const [servings, setServings] = useState(initialValues?.servings ?? 4);
  const [tags, setTags] = useState<string[]>(initialValues?.tags ?? []);
  const [tagInput, setTagInput] = useState("");
  const [ingredients, setIngredients] = useState<IngredientGroup[]>(
    initialValues?.ingredients ?? [{ group: "", items: [""] }]
  );
  const [steps, setSteps] = useState<RecipeStep[]>(
    initialValues?.steps ?? [{ stepNumber: 1, instruction: "", imageKey: null }]
  );
  const [notes, setNotes] = useState(initialValues?.notes ?? "");
  const [source, setSource] = useState(initialValues?.source ?? "");
  const [sourceUrl, setSourceUrl] = useState(initialValues?.sourceUrl ?? "");
  const [rating, setRating] = useState(initialValues?.rating ?? 0);
  const [isFavorite, setIsFavorite] = useState(initialValues?.isFavorite ?? false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(
    initialValues?.imageKey ? `${IMAGES_BASE}/${initialValues.imageKey}` : null
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleTitleChange(val: string) {
    setTitle(val);
    if (!slugEdited) setSlug(slugify(val));
  }

  function handleImageChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) setTags([...tags, t]);
    setTagInput("");
  }

  // Ingredients helpers
  function addIngredientGroup() {
    setIngredients([...ingredients, { group: "", items: [""] }]);
  }
  function removeIngredientGroup(gi: number) {
    setIngredients(ingredients.filter((_, i) => i !== gi));
  }
  function setGroupName(gi: number, val: string) {
    setIngredients(ingredients.map((g, i) => i === gi ? { ...g, group: val } : g));
  }
  function setIngredientItem(gi: number, ii: number, val: string) {
    setIngredients(ingredients.map((g, i) =>
      i === gi ? { ...g, items: g.items.map((it, j) => j === ii ? val : it) } : g
    ));
  }
  function addIngredientItem(gi: number) {
    setIngredients(ingredients.map((g, i) =>
      i === gi ? { ...g, items: [...g.items, ""] } : g
    ));
  }
  function removeIngredientItem(gi: number, ii: number) {
    setIngredients(ingredients.map((g, i) =>
      i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g
    ));
  }

  // Steps helpers
  function addStep() {
    setSteps([...steps, { stepNumber: steps.length + 1, instruction: "", imageKey: null }]);
  }
  function removeStep(i: number) {
    setSteps(steps.filter((_, j) => j !== i).map((s, j) => ({ ...s, stepNumber: j + 1 })));
  }
  function setStepInstruction(i: number, val: string) {
    setSteps(steps.map((s, j) => j === i ? { ...s, instruction: val } : s));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(
        {
          title,
          slug,
          description,
          course,
          cuisine,
          prepTimeMinutes: prepTime,
          cookTimeMinutes: cookTime,
          servings,
          tags,
          ingredients,
          steps,
          notes,
          source,
          sourceUrl,
          rating,
          isFavorite,
          imageKey: initialValues?.imageKey ?? "",
          thumbnailKey: initialValues?.thumbnailKey ?? "",
        },
        imageFile
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-8 flex flex-col gap-8">
      {error && (
        <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3">{error}</p>
      )}

      {/* Photo */}
      <section className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-gray-700">Photo</label>
        <div
          onClick={() => fileInputRef.current?.click()}
          className="relative cursor-pointer rounded-xl overflow-hidden bg-gray-100 border-2 border-dashed border-gray-300 hover:border-herb transition-colors flex items-center justify-center"
          style={{ aspectRatio: "16/9" }}
        >
          {imagePreview ? (
            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm text-gray-400">Click to upload a photo</span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageChange}
        />
      </section>

      {/* Title + Slug */}
      <section className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">Title <span className="text-red-500">*</span></span>
          <input
            required
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm text-gray-500">URL slug</span>
          <input
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setSlugEdited(true); }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-500 focus:outline-none focus:ring-2 focus:ring-herb"
          />
        </label>
      </section>

      {/* Description */}
      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-gray-700">Description</span>
        <textarea
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb resize-y"
        />
      </label>

      {/* Course + Cuisine */}
      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">Course</span>
          <select
            value={course}
            onChange={(e) => setCourse(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
          >
            <option value="">— select —</option>
            {COURSES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">Cuisine</span>
          <select
            value={cuisine}
            onChange={(e) => setCuisine(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
          >
            <option value="">— select —</option>
            {CUISINES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>

      {/* Time + Servings */}
      <div className="grid grid-cols-3 gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">Prep (min)</span>
          <input
            type="number" min={0}
            value={prepTime}
            onChange={(e) => setPrepTime(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">Cook (min)</span>
          <input
            type="number" min={0}
            value={cookTime}
            onChange={(e) => setCookTime(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">Servings</span>
          <input
            type="number" min={1}
            value={servings}
            onChange={(e) => setServings(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
          />
        </label>
      </div>

      {/* Tags */}
      <section className="flex flex-col gap-2">
        <span className="text-sm font-semibold text-gray-700">Tags</span>
        <div className="flex flex-wrap gap-2 mb-1">
          {tags.map((t) => (
            <span key={t} className="flex items-center gap-1 bg-herb text-white text-xs rounded-full px-3 py-1">
              {t}
              <button type="button" onClick={() => setTags(tags.filter((x) => x !== t))} className="hover:opacity-75">✕</button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(); } }}
            placeholder="Type + Enter to add"
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
          />
          <button type="button" onClick={addTag} className="px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200">Add</button>
        </div>
      </section>

      {/* Ingredients */}
      <section className="flex flex-col gap-4">
        <span className="text-sm font-semibold text-gray-700">Ingredients</span>
        {ingredients.map((group, gi) => (
          <div key={gi} className="border border-gray-200 rounded-xl p-4 flex flex-col gap-3">
            <div className="flex gap-2 items-center">
              <input
                value={group.group}
                onChange={(e) => setGroupName(gi, e.target.value)}
                placeholder="Group name (e.g. Sauce)"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
              />
              {ingredients.length > 1 && (
                <button type="button" onClick={() => removeIngredientGroup(gi)} className="text-gray-400 hover:text-red-500 text-sm">Remove group</button>
              )}
            </div>
            {group.items.map((item, ii) => (
              <div key={ii} className="flex gap-2">
                <input
                  value={item}
                  onChange={(e) => setIngredientItem(gi, ii, e.target.value)}
                  placeholder={`Ingredient ${ii + 1}`}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
                />
                {group.items.length > 1 && (
                  <button type="button" onClick={() => removeIngredientItem(gi, ii)} className="text-gray-400 hover:text-red-500 text-lg leading-none">✕</button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => addIngredientItem(gi)} className="text-sm text-herb hover:underline self-start">+ Add ingredient</button>
          </div>
        ))}
        <button type="button" onClick={addIngredientGroup} className="text-sm text-herb hover:underline self-start">+ Add ingredient group</button>
      </section>

      {/* Steps */}
      <section className="flex flex-col gap-3">
        <span className="text-sm font-semibold text-gray-700">Steps</span>
        {steps.map((step, i) => (
          <div key={i} className="flex gap-3">
            <span className="mt-2 min-w-[1.5rem] text-sm font-semibold text-herb">{step.stepNumber}.</span>
            <div className="flex-1 flex gap-2">
              <textarea
                rows={2}
                value={step.instruction}
                onChange={(e) => setStepInstruction(i, e.target.value)}
                placeholder={`Step ${step.stepNumber}`}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb resize-y"
              />
              {steps.length > 1 && (
                <button type="button" onClick={() => removeStep(i)} className="text-gray-400 hover:text-red-500 text-lg leading-none self-start mt-2">✕</button>
              )}
            </div>
          </div>
        ))}
        <button type="button" onClick={addStep} className="text-sm text-herb hover:underline self-start">+ Add step</button>
      </section>

      {/* Notes + Source */}
      <label className="flex flex-col gap-1">
        <span className="text-sm font-semibold text-gray-700">Notes</span>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb resize-y"
        />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">Source</span>
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">Source URL</span>
          <input
            type="url"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-herb"
          />
        </label>
      </div>

      {/* Rating + Favorite */}
      <section className="flex items-center gap-6">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-semibold text-gray-700">Rating</span>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(rating === star ? 0 : star)}
                className={`text-2xl leading-none transition-colors ${star <= rating ? "text-yellow-400" : "text-gray-300"}`}
              >
                ★
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer mt-3">
          <input
            type="checkbox"
            checked={isFavorite}
            onChange={(e) => setIsFavorite(e.target.checked)}
            className="w-4 h-4 accent-spice"
          />
          <span className="text-sm text-gray-700">Favorite</span>
        </label>
      </section>

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="bg-herb text-white rounded-xl py-3 font-semibold hover:bg-herb-light disabled:opacity-50 transition-colors"
      >
        {submitting ? "Saving…" : submitLabel}
      </button>
    </form>
  );
}
